import { getUserGetMyProfile, postUserUpdateMyProfile } from '@apis/Customers';
import { useCallback, useEffect, useState } from 'react';
import { useDi } from '@root/Services/DI';
import { Box, Button, Group, Loader, LoadingOverlay, Select, Space, Stack, Text, TextInput } from '@mantine/core';
import { Clearfix } from '@root/Design/Primitives';
import { UserImage } from '@root/Components/Users/UserImage';
import { User } from '@apis/Customers/model';
import { NotificationService } from '@root/Services/Notification/NotificationService';
import { SidePanel, SidePanelOpener } from '@root/Design/SidePanel';
import { useEvent, useEventValue } from '@root/Services/EventEmitter';
import { BasicRouteLoader } from '@root/Services/Router/BasicRouteLoader';
import { CurrencyPreferenceService, SupportedCurrency } from '@root/Services/Customers/CurrencyPreferenceService';
import { CurrencyService } from '@root/Services/CurrencyService';
import { CompanyConfigService } from '@root/Services/Customers/CompanyConfigService';
import { useCompany } from '../Router/CompanyContent';

interface UserProfileDrawerProps {
    sidePanelOpener: SidePanelOpener;
    onChanged?: (profile: User) => void;
}

export function UserProfile({ sidePanelOpener, onChanged }: UserProfileDrawerProps) {
    const [profile, setProfile] = useState<User>();
    const [loading, setLoading] = useState<boolean>(false);
    const [form, setForm] = useState({
        firstName: '',
        lastName: '',
    });
    const [currency, setCurrency] = useState<SupportedCurrency>('USD');
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const [saving, setSaving] = useState(false);
    const [availableCurrencies, setAvailableCurrencies] = useState<string[]>([]);
    const notificationSvc = useDi(NotificationService);
    const currencyPrefSvc = useDi(CurrencyPreferenceService);
    const company = useCompany();
    const currencySvc = useDi(CurrencyService);
    const companyConfigSvc = useDi(CompanyConfigService);
    const router = useDi(BasicRouteLoader);
    useEvent(router.routeMeta);
    const currentRoute = router.getTopRouteMeta().currentPathWithoutParams;

    const loadProfile = useCallback(async () => {
        setLoading(true);
        try {
            const user = await getUserGetMyProfile();
            setProfile(user);
            setForm({
                firstName: user?.FirstName ?? '',
                lastName: user?.LastName ?? '',
            });
            const currentCurrency = currencyPrefSvc.getCurrency();
            setCurrency(currentCurrency);

            // Load available currencies from company config
            const companyConfig = await companyConfigSvc.getTypedCompanyConfig();
            if (companyConfig.billingCurrencies) {
                try {
                    const currencies = JSON.parse(companyConfig.billingCurrencies) as string[];
                    // Always include USD if there are multiple currencies
                    if (currencies.length > 0 && !currencies.includes('USD')) {
                        currencies.push('USD');
                    }
                    setAvailableCurrencies(currencies);
                } catch {
                    setAvailableCurrencies([]);
                }
            } else {
                setAvailableCurrencies([]);
            }
        } finally {
            setLoading(false);
        }
    }, [company?.Id]);

    const onOpenEvt = useCallback((opened: boolean) => {
        if (opened) {
            loadProfile();
        }
    }, []);
    useEvent(sidePanelOpener.evt, onOpenEvt);

    const handleInputChange = (field: keyof typeof form, value: string) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    useEffect(() => {
        if (profile) {
            const profileChanged = form.firstName !== profile.FirstName || form.lastName !== profile.LastName;
            const currencyChanged = currency !== currencyPrefSvc.getCurrency();
            setIsDirty(profileChanged || currencyChanged);
        }
    }, [form, profile, currency, currencyPrefSvc]);

    const saveUserProfile = async (updatedProfile: User) => {
        const updatedUser = await postUserUpdateMyProfile(updatedProfile);
        setProfile(updatedUser);
        onChanged?.(updatedUser);
    };

    const save = async () => {
        setSaving(true);
        const userCopy = { ...profile, FirstName: form.firstName, LastName: form.lastName };
        try {
            await Promise.all([saveUserProfile(userCopy), currencyPrefSvc.setCurrency(currency)]);

            notificationSvc.notify('Profile Saved', '', 'success', null);
        } catch (error) {
            notificationSvc.notify('Profile Save Failed', '', 'error', null);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <SidePanel
                key={currentRoute}
                size={400}
                opener={sidePanelOpener}
                title="Edit User Profile"
                toolbar={
                    <>
                        <Button variant="outline" onClick={sidePanelOpener.close}>
                            Cancel
                        </Button>
                        <Button disabled={!isDirty} onClick={save} rightIcon={saving ? <Loader size="sm" color="gray.0" /> : null}>
                            Save
                        </Button>
                    </>
                }
            >
                <Stack sx={{ height: '100%' }}>
                    <>
                        <LoadingOverlay visible={loading} overlayBlur={2} />
                        <Group>
                            <Box>
                                <UserImage
                                    firstName={profile?.FirstName ?? ''}
                                    lastName={profile?.LastName ?? ''}
                                    email={profile?.EMail ?? ''}
                                    picture={profile?.Picture ?? ''}
                                    id={profile?.Id ?? 0}
                                    diameter={48}
                                />
                            </Box>
                            <div>
                                {(profile?.FirstName &&
                                    profile?.LastName &&
                                    profile?.FirstName.trim().length > 0 &&
                                    profile?.LastName.trim().length > 0) ||
                                profile?.EMail ? (
                                    <Text weight="bold">{profile?.FirstName + ' ' + profile?.LastName}</Text>
                                ) : (
                                    <Text weight="dim" italic>
                                        No Name
                                    </Text>
                                )}
                                <Text>{profile?.EMail}</Text>
                            </div>
                        </Group>
                        <Clearfix />
                        <Space h="md" />
                        <TextInput
                            label="First Name"
                            placeholder="First Name"
                            defaultValue={form.firstName}
                            onChange={(e) => handleInputChange('firstName', e.target.value)}
                        />
                        <Space h="md" />
                        <TextInput
                            label="Last Name"
                            placeholder="Last Name"
                            defaultValue={form.lastName}
                            onChange={(e) => handleInputChange('lastName', e.target.value)}
                        />
                        {availableCurrencies.length > 0 && (
                            <>
                                <Space h="md" />
                                <Select
                                    label="Preferred Currency"
                                    placeholder="Select currency"
                                    value={currency}
                                    onChange={(value) => setCurrency(value as SupportedCurrency)}
                                    data={availableCurrencies.map((code) => ({
                                        value: code,
                                        label: `${code} - ${currencySvc.getName(code)}`,
                                    }))}
                                    searchable
                                    description="Amounts will be automatically converted to this currency for select data, fields, and features."
                                />
                            </>
                        )}
                    </>
                </Stack>
            </SidePanel>
        </>
    );
}
